"use client"

import type React from "react"
import type { AnimationControls } from "framer-motion"
import type { TowerCube } from "@/types/tower"

export interface EasterEggAnimationOptions {
  tower: TowerCube[]
  controlsRef: React.MutableRefObject<AnimationControls[]>
  cubeSize: number
  includeBaseCube: boolean
  getPositions: (count: number) => number[]
  getZ: (verticalIndex: number) => number
  cubeFlipStates?: React.MutableRefObject<Record<string, boolean>>
}

// Debug mode for logging flip states
const DEBUG_FLIPS = false

export async function performEasterEggAnimation({
  tower,
  controlsRef,
  cubeSize,
  includeBaseCube,
  getPositions,
  getZ,
  cubeFlipStates = { current: {} },
}: EasterEggAnimationOptions): Promise<TowerCube[]> {
  const collectionStart = includeBaseCube ? 1 : 0
  const collectionIndices = Array.from({ length: tower.length - collectionStart }, (_, i) => i + collectionStart)

  // Improved shuffling to guarantee a different order
  const shuffledIndices = [...collectionIndices]
  let isIdentical = true

  // Keep shuffling until we get a different order
  while (isIdentical) {
    // Shuffle the indices
    for (let i = shuffledIndices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffledIndices[i], shuffledIndices[j]] = [shuffledIndices[j], shuffledIndices[i]]
    }

    // Check if at least one cube has changed position
    isIdentical = shuffledIndices.every((val, idx) => val === collectionIndices[idx])

    // If we only have 2 cubes and they're identical, force a swap
    if (isIdentical && shuffledIndices.length === 2) {
      ;[shuffledIndices[0], shuffledIndices[1]] = [shuffledIndices[1], shuffledIndices[0]]
      isIdentical = false
    }
  }

  // Map from cube idx to new vertical slot
  const targetSlot: Record<number, number> = {}
  shuffledIndices.forEach((idx, order) => {
    targetSlot[idx] = order + collectionStart // slot counting from base (0)
  })

  // Create a mapping from index to cube key for easier reference
  const indexToKey: Record<number, string> = {}
  tower.forEach((cube, idx) => {
    indexToKey[idx] = cube.key
  })

  if (DEBUG_FLIPS) {
    console.log("Starting Easter egg animation with cubes:", shuffledIndices)
    console.log("Initial flip states:", { ...cubeFlipStates.current })
  }

  /* 1. side‑step each cube (one by one) & bring to top while moving sideways */
  for (let step = 0; step < shuffledIndices.length; step++) {
    const idx = shuffledIndices[step]
    const dir = step % 2 === 0 ? -1 : 1
    const cubeKey = indexToKey[idx]
    const isFlipped = cubeFlipStates.current[cubeKey] || false

    // Move cube to side track while preserving its current flip state
    await controlsRef.current[idx].start({
      x: dir * cubeSize * 0.8,
      scaleX: isFlipped ? -1 : 1, // Ensure we maintain the correct flip state
      zIndex: 100 + tower.length + 5, // temporary very high layer
      transition: { duration: 0.2 }, // Reduced from 0.3
    })
  }

  /* 2. vertical shuffle (on side track) — update z before/with movement */
  for (const idx of shuffledIndices) {
    const vSlot = targetSlot[idx]
    const cubeKey = indexToKey[idx]
    const isFlipped = cubeFlipStates.current[cubeKey] || false

    // Move cube to its new vertical position while preserving its flip state
    await controlsRef.current[idx].start({
      bottom: getPositions(tower.length)[vSlot],
      scaleX: isFlipped ? -1 : 1, // Ensure we maintain the correct flip state
      zIndex: getZ(vSlot),
      transition: { duration: 0.2, ease: "easeInOut" }, // Reduced from 0.3
    })
  }

  /* 3. random flip (only while on side track) */
  // Add a small pause to ensure cubes are stable on the side track before flipping
  await new Promise((resolve) => setTimeout(resolve, 100)) // Reduced from 200

  // Now perform the random flips and track which cubes are flipped
  for (const idx of shuffledIndices) {
    const cubeKey = indexToKey[idx]
    // Decide whether to flip this cube (50% chance)
    const shouldFlip = Math.random() < 0.5

    if (shouldFlip) {
      // Toggle the flip state of this cube
      const currentFlipState = cubeFlipStates.current[cubeKey] || false
      cubeFlipStates.current[cubeKey] = !currentFlipState

      // Apply the flip animation - set scaleX to -1 if flipped, 1 if not flipped
      await controlsRef.current[idx].start({
        scaleX: cubeFlipStates.current[cubeKey] ? -1 : 1,
        transition: { duration: 0.15 }, // Reduced from 0.25
      })

      if (DEBUG_FLIPS) {
        console.log(
          `Flipped cube ${idx} (key: ${cubeKey}) on side track, new state: ${cubeFlipStates.current[cubeKey] ? "flipped" : "not flipped"}`,
        )
      }
    }
  }

  // Add another small pause before moving back to center
  await new Promise((resolve) => setTimeout(resolve, 150)) // Reduced from 300

  /* 4. merge back centre track *bottom→top* (order by final vertical slot) */
  const backOrder = [...shuffledIndices].sort((a, b) => targetSlot[a] - targetSlot[b])

  if (DEBUG_FLIPS) {
    console.log("Moving cubes back to center track in order:", backOrder)
    console.log("Current flip states:", { ...cubeFlipStates.current })
  }

  for (const idx of backOrder) {
    const vSlot = targetSlot[idx]
    const cubeKey = indexToKey[idx]
    const isFlipped = cubeFlipStates.current[cubeKey] || false

    if (DEBUG_FLIPS) {
      console.log(
        `Moving cube ${idx} (key: ${cubeKey}) back to center, flipped state: ${isFlipped ? "flipped" : "not flipped"}`,
      )
    }

    // Move cube back to center track with its current flip state
    await controlsRef.current[idx].start({
      x: 0,
      scaleX: isFlipped ? -1 : 1, // Explicitly set scaleX based on tracked flip state
      zIndex: getZ(vSlot),
      transition: { duration: 0.25, ease: "easeOut" }, // Reduced from 0.35
    })
  }

  /* 5. rebuild tower array with new keys to ensure React treats them as new components */
  const newTower: TowerCube[] = []

  // Keep the base cube with the same key
  if (includeBaseCube) {
    const baseCube = tower[0]
    newTower.push({
      ...baseCube,
      key: baseCube.key, // Keep the original key for the base cube
    })
  }

  // Add the collection cubes in their new order with their original keys
  backOrder.forEach((idx) => {
    const cube = tower[idx]
    newTower.push({
      ...cube,
      key: cube.key, // Keep the original key
    })
  })

  if (DEBUG_FLIPS) {
    console.log("Easter egg animation complete, final tower:", newTower)
    console.log("Final flip states:", { ...cubeFlipStates.current })
  }

  return newTower
}
